import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, AlertCircle, HelpCircle, Trophy } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ValidationItem {
  id: string;
  label: string;
  required: boolean;
  completed: boolean;
  helpText?: string;
}

interface ReadinessValidationChecklistProps {
  selectedPlayersCount: number;
  startingLineupCount: number;
  minimumPlayers?: number;
  minimumStarters?: number;
  sportName?: string;
  sportVariant?: string;
}

export const ReadinessValidationChecklist: React.FC<ReadinessValidationChecklistProps> = ({
  selectedPlayersCount,
  startingLineupCount,
  minimumPlayers = 1,
  minimumStarters = 1,
  sportName,
  sportVariant
}) => {
  const validationItems: ValidationItem[] = [
    {
      id: 'players-selected',
      label: `At least ${minimumPlayers} player(s) selected`,
      required: true,
      completed: selectedPlayersCount >= minimumPlayers,
      helpText: 'Select players from your team roster to participate in this game'
    },
    {
      id: 'starting-lineup',
      label: `At least ${minimumStarters} starter(s) designated`,
      required: true,
      completed: startingLineupCount >= minimumStarters,
      helpText: 'Mark which players will be in the starting lineup'
    }
  ];

  const allRequiredComplete = validationItems
    .filter(item => item.required)
    .every(item => item.completed);

  const getIcon = (item: ValidationItem) => {
    if (item.completed) {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    }
    if (item.required) {
      return <XCircle className="h-4 w-4 text-red-600" />;
    }
    return <AlertCircle className="h-4 w-4 text-yellow-600" />;
  };

  return (
    <Card className={allRequiredComplete ? "border-green-200 bg-green-50/30" : "border-yellow-200 bg-yellow-50/30"}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            {allRequiredComplete ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-yellow-600" />
            )}
            Readiness Checklist
          </CardTitle>
          {sportName && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Trophy className="h-3 w-3" />
              {sportName}
              {sportVariant && ` - ${sportVariant}`}
            </Badge>
          )}
        </div>
        <CardDescription>
          {sportVariant && (
            <span className="block mb-1">
              {sportVariant} ({minimumPlayers}v{minimumPlayers})
            </span>
          )}
          {allRequiredComplete
            ? 'All requirements met! You can confirm team readiness.'
            : 'Complete all required items before confirming readiness'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {validationItems.map(item => (
            <div key={item.id} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {getIcon(item)}
                <span className={`text-sm ${item.completed ? 'text-green-800' : 'text-gray-700'}`}>
                  {item.label}
                </span>
                {item.required && !item.completed && (
                  <span className="text-xs text-red-600 font-medium">Required</span>
                )}
              </div>
              {item.helpText && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">{item.helpText}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          ))}
        </div>

        {!allRequiredComplete && (
          <div className="mt-4 p-3 bg-yellow-100 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> The "Confirm Team Ready" button will be enabled once all required items are completed.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
